// ============================================================================
// state.js — egyszerű, függőség nélküli alkalmazásállapot + feliratkozás.
//
// A kezelőfelület nyelve (uiLang) ITT, a böngésző localStorage-ában
// tárolódik, NEM a Firestore-ban — ez szándékos: bejelentkezés ELŐTT is
// kell tudni választani, amikor még nincs hitelesített felhasználó, akihez
// szerveroldalon el lehetne tárolni a preferenciát. (Ha később a
// saveProfile callable elkészül, onnantól kezdve bejelentkezve lévő
// felhasználóknál a szerveren mentett érték is figyelembe vehető - ez a
// modul erre már felkészítve van a `applyServerUiLang()` függvénnyel.)
// ============================================================================

import { DEFAULT_UI_LANG, isSupportedUiLang } from "./i18n.js";

const STORAGE_KEY = "lineage0_ui_lang";
// Egymás mellett nézet (2026-10-19): az eredeti és a fordítás két oszlopban; a tag eszközén megjegyzett beállítás.
export const EGYMAS_MELLETT_KULCS = "lineage0_egymas_mellett";

function olvasdEgymasMellett() {
  try {
    return window.localStorage.getItem(EGYMAS_MELLETT_KULCS) === "1";
  } catch (e) {
    return false;
  }
}

function readStoredUiLang() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isSupportedUiLang(stored) ? stored : null;
  } catch (e) {
    // Privát böngészés / letiltott localStorage esetén sem szabad
    // elszállnia az alkalmazásnak - egyszerűen nincs mentett preferencia.
    return null;
  }
}

function writeStoredUiLang(lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // Nem kritikus - a választás csak az aktuális munkamenetre érvényes.
  }
}

const listeners = new Set();

const storedUiLang = readStoredUiLang();

export const state = {
  // Az induló nyelv az ANGOL (2026-10-13, tagi kérés) - nem a böngészőé; a mentett választás felülírja.
  uiLang: storedUiLang || DEFAULT_UI_LANG,
  uiLangChosen: storedUiLang !== null, // false = még nem választott, mutassuk a nyelvválasztó képernyőt
  // Ha már van mentett nyelvi preferencia (pl. Google-redirect utáni teljes
  // oldal-újratöltés, ahol a localStorage megmarad, de minden JS-állapot
  // nullázódik), NE a nyelvválasztó "lang" képernyővel induljunk - azt a
  // fő render() döntés kizárólag uiLangChosen alapján hagyja ki, és ha a
  // screen mégis "lang" maradna egy ilyen esetben, az egy nem kezelt
  // állapotba (hibás "default" ág) futtatná a nézetet. Ehelyett egy
  // valódi, kezelt kezdőállapotból indulunk, amíg a Firebase Auth
  // visszajelzése (watchAuthState) hamarosan felülírja a tényleges
  // bejelentkezési/jogosultsági állapottal.
  screen: storedUiLang !== null ? "access-checking" : "lang",
  loginLoading: false, // true, amíg a Google-átirányítás folyamatban van

  // --- Csevegési állapot ---
  contentLang: null,      // a csevegés nyelve (a profilból); null = még nem ismert
  contentLangs: {},       // {kód: megjelenítendő név} a config/languages-ből
  profileName: "",       // a megjelenített név
  profileNameLatn: "",   // a név latin betűs alakja (a tag adja meg)
  profileNameJa: "",     // a név japán írásmódja (a tag adja meg)
  rooms: [],              // a látható szobák
  currentRoomId: null,    // a megnyitott szoba
  // Melyik üzenetre válaszolunk épp? {messageId, senderName, senderNameLatn,
  // senderNameJa, excerpt} - a beviteli sáv fölötti jelzéshez és a küldéshez.
  replyTo: null,
  messages: null,         // null = még töltődik; [] = üres szoba
  hasOlder: false,        // van-e még régebbi, be nem töltött üzenet (lapozás)
  loadingOlder: false,    // épp töltődik-e egy régebbi lap
  unreadRoomIds: [],      // szobák, ahol olvasatlan üzenet van (olvasatlan.js)
  draft: "",              // a beviteli mező tartalma (küldési hiba után is megmarad)
  sending: false,
  chatErrorKey: null,
  chatErrorText: null,     // ritka eset: kész, nem azonosítóból jövő hibaszöveg
  pendingSendId: null,
  // a folyamatban lévő küldés azonosítója (idempotencia)
  elkuldottDarabok: 0,   // több részes küldésnél: hány darab ment már el
  ujraprobalhato: false, // a legutóbbi küldés hálózati okból bukott el
  overlay: null,          // null | "rooms" | "admin" | "profile"
  offerNotifications: false, // mutassuk-e az értesítés-felajánló sávot
  pendingImage: null,     // a küldésre előkészített kép (előnézettel)
  uploadProgress: null,   // 0-100, amíg a kép töltődik fel
  // Üzenet szerkesztése (2026-10-02): melyik üzenetet szerkeszti épp a
  // felhasználó, és mi a beviteli mező tartalma hozzá. `null`, ha senkit.
  editingMessageId: null,
  editingText: "",
  editingBusy: false,
  // Eredeti/fordítás átváltása és folyamatban lévő újrafordítás (2026-10-07): üzenetazonosítók.
  // SET (nem tömb): a chatView ezt várja; a változtatás mindig ÚJ Set-tel történik.
  showOriginalIds: new Set(),
  egymasMellett: olvasdEgymasMellett(),
  // Bejelentkezési / jogosultság-hibakód (diagnostika.js) és a kézi újrapróbálások száma a jogosultság-ellenőrzésnél (2026-10-19, a titkár #46 kérése).
  authDiag: null,
  accessProbak: 0,
  retryBusyIds: new Set(),
  // Karakterszavazó (2026-10-30): üzenetazonosító → { fazis: "toltes"|"kesz"|"hiba", mine, closed, canSeeResults, canClose, results, voterCount, mentes }
  szavazok: {},
  // Átirat (2026-10-19): a következő hozzászólás idegen nyelvű átirat (nyelvkód) vagy null; a megnyitott javító szerkesztő vázlata.
  atiratNyelv: null,
  atiratSzerk: null,
  // A címzettnek szóló cetlik (2026-10-07, noteData.js) - legújabb elöl, legfeljebb 5.
  notes: [],
  editingErrorKey: null,
  user: null, // Firebase Auth User objektum bejelentkezés után
  access: null, // { active, role } vagy null, amíg nem ismert
  error: null, // { key, params } vagy null
};

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) fn(state);
}

/**
 * Nyelv KIVÁLASZTÁSA a nyelvválasztó képernyőn: a nyelvet állítja, de a
 * képernyőt NEM lépteti tovább - a felhasználó a "Tovább" gombbal lép
 * onnan el (confirmUiLang).
 *
 * FONTOS, VALÓS HIBÁBÓL TANULVA: korábban ez a függvény a uiLangChosen
 * jelzőt is true-ra állította, amitől a render() átugrotta a
 * nyelvválasztó ágat, miközben a screen még "lang" maradt - ami nem
 * szerepel a switch ágai között, így azonnal hibaképernyőre vitt.
 * Most a screen az EGYETLEN igazságforrás arról, melyik képernyő
 * látszik, a uiLangChosen pedig csak azt jelzi, választott-e már valaha.
 */
export function setUiLang(lang) {
  if (!isSupportedUiLang(lang)) return;
  state.uiLang = lang;
  writeStoredUiLang(lang);
  notify();
}

/** A nyelvválasztó képernyő lezárása ("Tovább" gomb). */
export function confirmUiLang() {
  state.uiLangChosen = true;
  // A JÓVÁHAGYOTT nyelvet MENTJÜK (2026-10-10, valós hiba): korábban csak a legördülő módosítása mentett (setUiLang), a
  // "Tovább" gomb nem - aki a böngészője nyelvét hagyta jóvá, annak a nyelvválasztó MINDEN indításnál újra megjelent,
  // a Google-ról visszatérve (teljes oldalújratöltés) is, és onnan a belépő képernyőre dobta vissza -> bejelentkezési hurok.
  writeStoredUiLang(state.uiLang);
  // Aki már be van jelentkezve (az átirányításos bejelentkezésből visszatérve a hitelesítés a nyelvválasztó alatt már
  // megtörtént), azt NEM dobjuk vissza a belépő képernyőre: a bejelentkezés utáni útvonalat a watchAuthState állítja be.
  // Kijelentkezve a "Tovább" az üdvözlő képernyőre visz (2026-10-13), onnan a "Belépek" a Google-bejelentkezésre.
  if (!state.user) state.screen = "welcome";
  notify();
}

/**
 * Later hook: ha egy jövőbeli saveProfile callable szerveren is tárolna
 * kezelőfelület-nyelv preferenciát, ez alkalmazná bejelentkezéskor -
 * jelenleg nincs meghívva sehonnan, csak elő van készítve.
 */
export function applyServerUiLang(lang) {
  if (isSupportedUiLang(lang)) {
    state.uiLang = lang;
    writeStoredUiLang(lang);
    notify();
  }
}

export function setScreen(screen) {
  state.screen = screen;
  state.error = null;
  // Képernyőváltáskor a "bejelentkezés folyamatban" jelzés mindig lejár -
  // enélkül egy sikertelen redirect után beragadhatna a letiltott gomb.
  state.loginLoading = false;
  notify();
}

export function setLoginLoading(loading) {
  state.loginLoading = loading;
  notify();
}

export function setUser(user) {
  state.user = user;
  notify();
}

export function setAccess(access) {
  state.access = access;
  notify();
}

export function setError(key, params) {
  state.error = key ? { key, params } : null;
  notify();
}

// ---------------------------------------------------------------------------
// Csevegési állapot módosítói
// ---------------------------------------------------------------------------

export function setChat(patch) {
  // VALÓS HIBÁBÓL TANULVA (2026-10-03): a *ErrorKey/*ErrorText mezőpárok
  // közül bármelyik hívó csak az EGYIKET adta meg (jellemzően a fordítási
  // kulcsot), a másik pedig - mivel a setChat egyszerű Object.assign - egy
  // KORÁBBI hívásból ott ragadt. Mivel a megjelenítés mindig a RAW szöveget
  // (*ErrorText) részesíti előnyben, ha van, egy régi, oda nem illő szöveg
  // ÍRTA FELÜL a frissen beállított, új kulcs szerinti feliratot. Ezért: ha
  // egy patch megadja a kulcsot, de a szöveget nem, a szöveg KIFEJEZETTEN
  // nullázódik - egy új hiba sosem örökölheti egy régi hiba RAW szövegét.
  const patched = { ...patch };
  if ("chatErrorKey" in patched && !("chatErrorText" in patched)) patched.chatErrorText = null;
  if ("editingErrorKey" in patched && !("editingErrorText" in patched)) patched.editingErrorText = null;
  Object.assign(state, patched);
  notify();
}

export function setOverlay(overlay) {
  state.overlay = overlay;
  notify();
}
